import React, { useState, useEffect } from "react";
import { Calendar, FileText, CheckCircle2 } from "lucide-react";
import {
  createAcademicYear,
  updateAcademicYear,
} from "../../api/academicyear/academicYearApi";
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  Modal,
  Alert,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const AcademicYearFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  editData = null,
}) => {
  const isEdit = Boolean(editData);

  const [formData, setFormData] = useState({
    name: "",
    startDate: "",
    endDate: "",
    description: "",
    isActive: true,
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setApiError("");
      setErrors({});

      if (editData) {
        setFormData({
          name: editData.name || editData.title || editData.yearName || "",
          startDate: editData.startDate
            ? editData.startDate.substring(0, 10)
            : "",
          endDate: editData.endDate ? editData.endDate.substring(0, 10) : "",
          description: editData.description || editData.notes || "",
          isActive:
            editData.isActive !== undefined ? Boolean(editData.isActive) : true,
        });
      } else {
        setFormData({
          name: "",
          startDate: "",
          endDate: "",
          description: "",
          isActive: true,
        });
      }
    }
  }, [isOpen, editData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Academic year name is required";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (start >= end) {
        newErrors.endDate = "End date must be after start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        yearName: formData.name.trim(),
        startDate: formData.startDate,
        endDate: formData.endDate,
      };

      if (isEdit) {
        await updateAcademicYear(editData._id || editData.id, payload);
        toast.success("Academic year updated successfully");
      } else {
        await createAcademicYear(payload);
        toast.success("Academic year created successfully");
      }

      setTimeout(() => {
        onSuccess();
        onClose();
      }, 800);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Something went wrong. Please try again.";
      setApiError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit Academic Year" : "Create Academic Year"}
      footer={
        <div className="flex w-full items-center justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="academic-year-form"
            variant="primary"
            isLoading={loading}
          >
            {isEdit ? "Update" : "Create"}
          </Button>
        </div>
      }
    >
      <form
        id="academic-year-form"
        onSubmit={handleSubmit}
        className="space-y-4 py-1"
      >
        {apiError && <Alert variant="danger">{apiError}</Alert>}

        

        <Input
          label="Academic Year Name *"
          id="name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g. 2026-2027"
          error={errors.name}
          disabled={loading}
          leftIcon={<Calendar className="h-4 w-4 text-neutral-400 dark:text-neutral-500" />}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Start Date *"
            id="startDate"
            name="startDate"
            type="date"
            value={formData.startDate}
            onChange={handleChange}
            error={errors.startDate}
            disabled={loading}
          />

          <Input
            label="End Date *"
            id="endDate"
            name="endDate"
            type="date"
            value={formData.endDate}
            onChange={handleChange}
            error={errors.endDate}
            disabled={loading}
          />
        </div>

        <Textarea
          label="Description / Notes"
          id="description"
          name="description"
          rows={3}
          value={formData.description}
          onChange={handleChange}
          placeholder="Optional notes about this academic year"
          disabled={loading}
        />

        <div className="pt-1">
          <Checkbox
            id="isActive"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            label="Mark as active status"
            disabled={loading}
          />
        </div>
      </form>
    </Modal>
  );
};

export default AcademicYearFormModal;